import { FC, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  Button,
  DataTable,
  InlineNotification,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
} from '@carbon/react';
import { CheckmarkOutline, Close } from '@carbon/react/icons';
import { formatResource } from 'utils/misc';
import { StatusTag, type PartyStatus } from 'components/status-tag';
import { fmtAmount } from '../format';
import type { RcSectionStatus } from './types';
import type { FinancialSummaryFields } from './types-financial-summary';
import {
  confirmFinancialSummaryAtom,
  createFinancialSummaryResourceAtom,
  financialSummaryUrl,
  revertFinancialSummaryAtom,
} from './atoms/financial-summary';
import shell from 'pages/afe-catalog/structuring/structuring-module.module.scss';
import styles from 'pages/afe-catalog/structuring/identification.module.scss';

interface Props {
  documentId: number | string;
}

const toPartyStatus = (status: RcSectionStatus): PartyStatus => {
  return status === 'complete' ? 'complete' : 'not_started';
};

const FIELDS: { key: keyof FinancialSummaryFields; label: string }[] = [
  { key: 'ownerGrossValue', label: 'Owner Gross Value' },
  { key: 'ownerTaxes', label: 'Owner Taxes' },
  { key: 'totalDeductions', label: 'Total Deductions' },
  { key: 'ownerNetValue', label: 'Owner Net Value' },
];

const HEADERS = [
  { key: 'field', header: 'Field' },
  { key: 'value', header: 'Value' },
];

// Read-only Check Financial Summary — mirror of the Overview section minus the
// Edit affordance (the values are citation-backed and only verified/confirmed).
// Renders nothing for non-PDF checks (available=false).
export const FinancialSummarySection: FC<Props> = ({ documentId }) => {
  const detailHref = financialSummaryUrl(documentId);
  const detailAtom = useMemo(
    () => createFinancialSummaryResourceAtom(detailHref),
    [detailHref]
  );
  const [detailResource, reloadDetail] = useAtom(detailAtom);
  const { loading, error, data: detail } = formatResource(detailResource);

  const confirmSummary = useSetAtom(confirmFinancialSummaryAtom);
  const revertSummary = useSetAtom(revertFinancialSummaryAtom);
  const [actionError, setActionError] = useState<string | null>(null);

  const status = detail?.status ?? 'not_started';
  const isReviewed = status === 'complete';
  const confirmHref = detail?.resources?.confirmation?.href;

  const rows = useMemo(() => {
    if (!detail) return [];
    return FIELDS.map((def) => ({
      id: def.key,
      field: def.label,
      value: fmtAmount(detail.fields[def.key]),
    }));
  }, [detail]);

  // Hidden for non-PDF checks (the summary citations only exist on the PDF path).
  if (detail && !detail.available) return null;

  const handleMarkCorrect = () => {
    if (!confirmHref) return;
    setActionError(null);
    confirmSummary({
      url: confirmHref,
      method: 'POST',
      onSuccess: () => reloadDetail(),
      onError: (m) => setActionError(m ?? 'Failed to mark as correct.'),
    });
  };

  const handleRemoveCorrect = () => {
    if (!confirmHref) return;
    setActionError(null);
    revertSummary({
      url: confirmHref,
      method: 'DELETE',
      onSuccess: () => reloadDetail(),
      onError: (m) => setActionError(m ?? 'Failed to remove correct status.'),
    });
  };

  return (
    <div className={shell.overviewBody}>
      <div className={shell.overviewSectionTitleRow}>
        <h4 className={shell.overviewSectionTitle}>Check Financial Summary</h4>
        <StatusTag
          status={toPartyStatus(status)}
          progress={isReviewed ? '1/1' : '0/1'}
        />
      </div>

      {actionError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={actionError}
          onCloseButtonClick={() => setActionError(null)}
        />
      )}
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Unable to load financial summary"
          subtitle={error}
        />
      )}

      {loading && !detail ? (
        <Loading
          withOverlay={false}
          small
          description="Loading financial summary…"
        />
      ) : (
        detail && (
          <DataTable rows={rows} headers={HEADERS}>
            {({
              rows: dtRows,
              headers,
              getHeaderProps,
              getTableProps,
            }: any) => (
              <TableContainer>
                <TableToolbar size="sm">
                  <TableToolbarContent>
                    {isReviewed ? (
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={Close}
                        onClick={handleRemoveCorrect}
                      >
                        Remove Correct Status
                      </Button>
                    ) : (
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={CheckmarkOutline}
                        disabled={!confirmHref}
                        onClick={handleMarkCorrect}
                      >
                        Mark as Correct
                      </Button>
                    )}
                  </TableToolbarContent>
                </TableToolbar>
                <Table
                  {...getTableProps()}
                  size="sm"
                  className={styles.identTable}
                >
                  <TableHead>
                    <TableRow>
                      {headers.map((h: any) => (
                        <TableHeader
                          {...getHeaderProps({ header: h })}
                          key={h.key}
                        >
                          {h.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dtRows.map((row: any) => (
                      <TableRow key={row.id}>
                        {row.cells.map((cell: any) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        )
      )}
    </div>
  );
};
